import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "232",
  "slug": "what-is-gis-why-should-you-care",
  "title": "What Is GIS and Why Should You Care?",
  "excerpt": "Geographic Information Systems power everything from emergency response to climate modeling to the apps on your phone. Here's what GIS actually is, how it works, and why it matters.",
  "tldr": "GIS stands for Geographic Information System: software that layers, analyses, and visualises data tied to specific locations on Earth. You should care because almost every infrastructure decision — where to put a new road, hospital, cell tower, fire station, or wind farm — runs through GIS, and the skills are increasingly valuable in tech, climate, logistics, and government roles.",
  "date": "2026-03-08",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800&q=80",
  "readTime": "2 min",
  "content": [
    {
      "type": "paragraph",
      "text": "When Hurricane Katrina struck New Orleans in 2005, emergency responders used a technology most people had never heard of. When epidemiologists tracked COVID-19 from Wuhan to the world, they used the same technology. When your phone routes you around a traffic jam, you benefit from the same technology. That technology is GIS — Geographic Information Systems — and it is quietly everywhere."
    },
    {
      "type": "heading",
      "text": "The Core Idea: Everything Happens Somewhere"
    },
    {
      "type": "paragraph",
      "text": "The fundamental insight of GIS is deceptively simple: almost all data has a location, and location matters. A patient record isn't just a medical history — it's tied to a place. A crime report happened at a specific address, in a specific neighborhood. GIS is the technology of collecting, storing, analyzing, and visualizing this spatial dimension of data."
    },
    {
      "type": "heading",
      "text": "How GIS Actually Works"
    },
    {
      "type": "list",
      "items": [
        "Vector data represents discrete features as points, lines, and polygons — buildings, roads, districts",
        "Raster data represents continuous phenomena as grids — elevation, temperature, satellite imagery",
        "Spatial analysis tools answer location-based questions: buffer analysis, overlay analysis, network analysis",
        "Geodatabases store spatial data with coordinate reference systems",
        "Remote sensing integrates satellite and aerial imagery into GIS workflows"
      ]
    },
    {
      "type": "heading",
      "text": "Who Uses GIS and For What"
    },
    {
      "type": "paragraph",
      "text": "Urban planners model growth scenarios. Public health officials track disease spread. Climate scientists model sea level rise. Agriculture uses GIS for precision farming. Insurance companies model disaster risk. Retail companies choose store locations. Conservation organizations identify priority habitats. The technology has penetrated virtually every field that deals with the physical world."
    },
    {
      "type": "heading",
      "text": "Why This Matters for Everyone"
    },
    {
      "type": "paragraph",
      "text": "As spatial data becomes more central to how decisions are made, the ability to read and critically evaluate maps becomes a modern literacy skill. Games like EarthGuessr build a piece of that literacy — the ability to read the Earth's surface from satellite imagery. The more people can read the world from above, the better equipped they are to understand the spatial dimensions of the challenges we face."
    },
    {
      "type": "heading",
      "text": "Getting Started with GIS"
    },
    {
      "type": "paragraph",
      "text": "QGIS is a full-featured, professional-grade GIS platform that is completely free and open source. Google Earth Engine provides cloud-based access to petabytes of satellite imagery. The barrier to entry has collapsed in the last decade, and the learning resources have multiplied to match."
    }
  ]
};
